'use client';

import React from 'react';
import { Search, Home, BookOpen, Users, ArrowLeft, Headphones } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

export default function NotFoundContent() {
    const pathname = usePathname();
    const isCourseUrl = pathname?.includes('/course');
    const isSchoolUrl = pathname?.includes('/school');
    const handleBack = () => {
        if (typeof window !== 'undefined') {
            window.history.back();
        }
    };

    return (
        <div className="min-h-[80vh] bg-white flex items-center justify-center px-4 py-8 sm:py-12">
            <div className="max-w-4xl w-full">
                <div className="text-center mb-8 sm:mb-12">
                    {/* Simple 404 */}
                    <div className="relative inline-block mb-6 sm:mb-8">
                        <h1 className="text-[120px] sm:text-[180px] md:text-[220px] lg:text-[240px] font-bold text-[#FFA201] leading-none select-none">
                            404
                        </h1>
                    </div>

                    {/* Message */}
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-800 mb-3 sm:mb-4 px-4">
                        Page Not Found
                    </h2>
                    <p className="text-base sm:text-lg text-gray-600 max-w-md mx-auto mb-6 sm:mb-8 px-4">
                        The page you're looking for doesn't exist or has been moved. Let's get you back on track.
                    </p>
                </div>

                {isCourseUrl && ( 
                <div className="text-center">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-800 mb-3 sm:mb-4 px-4">
                        Explore other courses
                    </h2>
                </div>
                )}

                {isSchoolUrl && ( 
                <div className="text-center">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-800 mb-3 sm:mb-4 px-4">
                        Explore our schools
                    </h2>
                </div>
                )}
                

                {/* Quick Links */}
                {!isSchoolUrl && (
                <div className={`grid grid-cols-1 ${isCourseUrl ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-3 sm:gap-4 mb-6 sm:mb-8 mx-auto px-4 sm:px-0 ${isCourseUrl ? 'max-w-3xl' : 'max-w-lg'}`}>
                    {isCourseUrl ? (
                        <>
                            <QuickLinkCard
                                icon={<BookOpen className="w-5 h-5 sm:w-6 sm:h-6" />}
                                title="Undergraduate"
                                description="Explore UG programmes"
                                href="/undergraduate"
                            />
                            <QuickLinkCard
                                icon={<BookOpen className="w-5 h-5 sm:w-6 sm:h-6" />}
                                title="Post Graduate"
                                description="Explore PG programmes"
                                href="/postgraduate"
                            />
                            <QuickLinkCard
                                icon={<BookOpen className="w-5 h-5 sm:w-6 sm:h-6" />}
                                title="Diploma"
                                description="Explore Diploma programmes"
                                href="/diploma"
                            />
                        </>
                    ) : (
                        <>
                            <QuickLinkCard
                                icon={<Home className="w-5 h-5 sm:w-6 sm:h-6" />}
                                title="Home"
                                description="Return to homepage"
                                href="/"
                            />
                            <QuickLinkCard
                                icon={<Headphones className="w-5 h-5 sm:w-6 sm:h-6" />}
                                title="Contact Support"
                                description="Get help"
                                href="/contact-us"
                            />
                        </>
                    )}
                </div>
                )}

                {isSchoolUrl && (
                <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6 sm:mb-8 mx-auto px-4 sm:px-0 max-w-4xl">
                    <SmallQuickLinkCard title="School of Health Science & Technology" href="/school/school-of-health-science-and-technology" />
                    <SmallQuickLinkCard title="School of Computer Science & Engineering" href="/school/school-of-computer-science-and-engineering" />
                    <SmallQuickLinkCard title="School of Hospitality & Tourism" href="/school/school-of-hospitality-and-tourism" />
                    <SmallQuickLinkCard title="School of Pharmacy" href="/school/school-of-pharmacy" />
                    <SmallQuickLinkCard title="School of Management & Commerce" href="/school/school-of-management-and-commerce" />
                    <SmallQuickLinkCard title="College of Nursing" href="/school/college-of-nursing" />
                    <SmallQuickLinkCard title="School of Humanities" href="/school/school-of-humanities" />
                </div>
                )}

                {/* Back Button */}
                <div className="text-center mt-6 sm:mt-8 px-4">
                    {/* <button
                        onClick={handleBack}
                        className="inline-flex items-center gap-2 text-[#FFA201] hover:text-[#FF8A00] font-medium transition-colors text-sm sm:text-base cursor-pointer border-none bg-transparent"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Go back to previous page
                    </button> */}
                </div>
            </div>
        </div>
    );
}

function QuickLinkCard({ icon, title, description, href }) {
    return (
        <Link
            href={href}
            className="group bg-white p-5 sm:p-6 border border-gray-200 hover:border-[#FFA201] transition-all duration-200 flex flex-col items-center text-center space-y-2 no-underline"
        >
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-50 group-hover:bg-[#FFA201]/10 flex items-center justify-center text-[#FFA201] transition-colors">
                {icon}
            </div>
            <h3 className="font-semibold text-gray-800 text-base sm:text-lg">{title}</h3>
            <p className="text-xs sm:text-sm text-gray-600">{description}</p>
        </Link>
    );
}

function SmallQuickLinkCard({ title, href }) {
    return (
        <Link
            href={href}
            className="group bg-white px-4 py-3 sm:px-6 sm:py-4 border border-gray-200 hover:border-[#FFA201] transition-all duration-200 flex items-center justify-center text-center no-underline rounded-lg shadow-sm hover:shadow-md"
        >
            <h3 className="font-semibold text-gray-800 text-sm sm:text-base group-hover:text-[#FFA201] transition-colors">{title}</h3>
        </Link>
    );
}
